import { createSelector } from "@reduxjs/toolkit";
import {
  RewardsAccount,
  cardsToShowWithPriorityOnRewardsBanner,
  cardsToNotShowBannerOn,
} from "@hopper-b2b/types";
import { IStoreState } from "../../../../reducers/types";

export const getIsFirstLaunch = (state: IStoreState) =>
  state.rewards.isFirstLaunch;

export const getAgentEmail = (state: IStoreState) => state.rewards.agentEmail;

export const getRewardsAccounts = (state: IStoreState) =>
  state.rewards.rewardsAccounts;

export const getUserSelectedAccountReferenceId = (state: IStoreState) =>
  state.rewards.selectedAccountReferenceId;

export const getFetchRewardsAccountsCallState = (state: IStoreState) =>
  state.rewards.fetchRewardsAccountsCallState;

export const getSortedRewardsAccounts = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount[] =>
    rewardsAccounts.sort((prev, current) => {
      return current.earn.hotelsMultiplier - prev.earn.hotelsMultiplier;
    })
);

export const getRewardsAccountWithLargestValue = createSelector(
  getSortedRewardsAccounts,
  (sortedRewardsAccounts): RewardsAccount | undefined => {
    return sortedRewardsAccounts[0];
  }
);

export const getSortedRewardsAccountWithLargestEarnForBanner = createSelector(
  getRewardsAccounts,
  (rewardsAccounts): RewardsAccount[] => {
    const earnMultiplier = (account: RewardsAccount) =>
      account.earn.flightsMultiplier ?? account.earn.hotelsMultiplier ?? -1;
    return rewardsAccounts.sort((prev, current) => {
      if (
        cardsToNotShowBannerOn.includes(
          current.productDisplayName.toLowerCase()
        )
      )
        return -1;

      if (earnMultiplier(current) === earnMultiplier(prev)) {
        return cardsToShowWithPriorityOnRewardsBanner.includes(
          current.productDisplayName.toLowerCase()
        )
          ? 1
          : -1;
      }
      return earnMultiplier(current) - earnMultiplier(prev);
    });
  }
);

export const getRewardsAccountWithLargestEarnForBanner = createSelector(
  getSortedRewardsAccountWithLargestEarnForBanner,
  (sortedRewardsAccount): RewardsAccount => {
    return sortedRewardsAccount[0];
  }
);

// the rewards account selection doesn't have a default state where none of the available options is selected
export const getDefaultSelectedAccountReferenceId = createSelector(
  getSortedRewardsAccounts,
  (rewardsAccounts): string | null => {
    // return the id with largest value
    if (rewardsAccounts.length > 0) {
      return rewardsAccounts[0].accountReferenceId;
    }

    return null;
  }
);

// this is the primary selector used outside the component
// either returns the user selected account id, or the default from above if not present
export const getSelectedAccountReferenceId = createSelector(
  getUserSelectedAccountReferenceId,
  getDefaultSelectedAccountReferenceId,
  (
    userSelectedAccountReferenceId,
    defaultSelectedAccountReferenceId
  ): string | null => {
    return userSelectedAccountReferenceId ?? defaultSelectedAccountReferenceId;
  }
);

export const getSelectedAccount = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (rewardsAccounts, referenceId): RewardsAccount | undefined => {
    return rewardsAccounts.find(
      (account) => account.accountReferenceId === referenceId
    );
  }
);

export const getSelectedAccountIndex = createSelector(
  getRewardsAccounts,
  getSelectedAccountReferenceId,
  (accounts, id) => {
    return accounts.findIndex((account) => account.accountReferenceId === id);
  }
);

export const getRewardsUserProperties = createSelector(
  getSelectedAccount,
  (
    rewardsAccount
  ): { product: string | undefined; currency: string | undefined } => {
    return {
      product: rewardsAccount?.productDisplayName,
      currency: rewardsAccount?.rewardsBalance.currency,
    };
  }
);
